import { useAuth } from '../auth/authContext'
import { useCallerAsset } from '../features/profile/useCallerAsset'
import { useOmniStatusMap } from './useOmniStatusMap'

/**
 * Current-user source for the home compositions.
 *
 * `name` comes from the BFF asset record (firstname + lastname) for the caller's
 * asset, falling back to the Keycloak token claim, then the email local-part.
 * `email` is the signed-in Keycloak claim. `assetId` comes from the BFF profile
 * fetch (GET /me/asset-data → caller.assetId); it's undefined until that query
 * resolves. `status` drives
 * which home composition renders (see screens/home/HomeScreen) and which shell
 * wraps it (see components/PrimaryShell); it resolves the asset row's Omnidek
 * status code through GET /me/omni/statusmap — see STATUS_BY_NAME.
 */
export type UserStatus = 'prospect' | 'applicant' | 'participant'

export interface CurrentUser {
  name: string
  email?: string
  status: UserStatus
  assetId?: string
}

/**
 * Force a status during development, bypassing the live BFF value — set it to a
 * UserStatus to render that path everywhere, or `null` to use the real status.
 * DEV-only: `import.meta.env.DEV` is statically false in a production build, so
 * this can't leak into prod even if it's left set.
 */
const DEV_STATUS_OVERRIDE: UserStatus | null = null

/**
 * Omnidek status display name (lowercased) → the composition we render for it.
 * Only the three we've built compositions for are listed; every other code the
 * map can return — Awardee, Pre-Awardee, Paid-Off, Withdrawn, Lead — falls back
 * to `prospect`, the least-privileged view. Add a key here as each status gets
 * its own screens.
 */
const STATUS_BY_NAME: Record<string, UserStatus> = {
  prospect: 'prospect',
  applicant: 'applicant',
  participant: 'participant',
}

export function useCurrentUser(): CurrentUser {
  const { claims } = useAuth()
  const { asset, assetId } = useCallerAsset()
  const { data: statusMap } = useOmniStatusMap()

  const assetName = [asset?.firstname, asset?.lastname]
    .filter(Boolean)
    .join(' ')
    .trim()

  const name =
    assetName || claims?.name?.trim() || claims?.email?.split('@')[0] || 'there'

  const statusName =
    asset?.status == null ? undefined : statusMap?.byCode?.[String(asset.status)]
  const liveStatus = STATUS_BY_NAME[statusName?.trim().toLowerCase() ?? ''] ?? 'prospect'
  const status =
    import.meta.env.DEV && DEV_STATUS_OVERRIDE ? DEV_STATUS_OVERRIDE : liveStatus

  return { name, email: claims?.email, status, assetId }
}
